"use client";

import React from "react";
import { ShieldCheck } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
const policySections = [{
  id: "data-collection",
  title: "1. Data Collection & Encryption",
  content: <div className="space-y-4">
        <p>
          We implement rigorous data collection protocols designed to meet federal security standards. All information transmitted through our systems is protected by advanced encryption methods:
        </p>
        <ul className="list-disc pl-5 space-y-2">
          <li><strong>AES-256 Encryption:</strong> All sensitive client data is encrypted at rest using military-grade AES-256 standards.</li>
          <li><strong>TLS 1.3 Protocols:</strong> Data in transit is secured via Transport Layer Security (TLS) 1.3 to prevent interception.</li>
          <li><strong>AI-Driven Monitoring:</strong> Our systems utilize automated AI to detect and block unauthorized data access attempts in real-time.</li>
        </ul>
      </div>
}, {
  id: "data-usage",
  title: "2. Federal Compliance & Usage",
  content: <div className="space-y-4">
        <p>
          Your information is utilized strictly for security auditing and compliance verification purposes, ensuring alignment with federal mandates:
        </p>
        <ul className="list-disc pl-5 space-y-2">
          <li>Conducting comprehensive vulnerability assessments and penetration testing.</li>
          <li>Generating compliance reports for NIST, SOC2, and HIPAA regulatory frameworks.</li>
          <li>Providing real-time security alerts and administrative system updates.</li>
          <li>Improving our AI threat detection algorithms through anonymized metadata analysis.</li>
          <li>Maintaining an immutable audit trail for all security-related transactions.</li>
        </ul>
      </div>
}, {
  id: "data-security",
  title: "3. Infrastructure Security Standards",
  content: <div className="space-y-4">
        <p>
          Our infrastructure is built on a zero-trust architecture, ensuring that every access request is fully authenticated and authorized:
        </p>
        <ul className="list-disc pl-5 space-y-2">
          <li><strong>Multi-Factor Authentication:</strong> Mandatory MFA for all personnel accessing internal security systems.</li>
          <li><strong>Air-Gapped Backups:</strong> Critical security data is stored in offline, air-gapped environments to prevent ransomware.</li>
          <li><strong>Continuous Auditing:</strong> 24/7 monitoring by our Security Operations Center (SOC) to identify potential vulnerabilities.</li>
        </ul>
        <p>
          We maintain strict physical and digital barriers to ensure that your proprietary security data remains confidential and isolated.
        </p>
      </div>
}, {
  id: "user-rights",
  title: "4. Client Data Protection Rights",
  content: <div className="space-y-4">
        <p>
          Under federal data protection guidelines, our clients retain full sovereignty over their security information and audit logs:
        </p>
        <ul className="list-disc pl-5 space-y-2">
          <li><strong>Right to Audit:</strong> Clients may request detailed logs of how their data has been accessed or processed.</li>
          <li><strong>Right to Data Portability:</strong> Secure transfer of audit findings to authorized third-party compliance officers.</li>
          <li><strong>Right to Immediate Deletion:</strong> Permanent, forensic-level erasure of data upon contract termination.</li>
          <li><strong>Right to Processing Restriction:</strong> Ability to limit data analysis to specific geographic regions or jurisdictions.</li>
        </ul>
        <p>
          To exercise these rights, please submit a formal request through our secure client portal or contact our compliance officer.
        </p>
      </div>
}, {
  id: "third-party",
  title: "5. Third-Party Disclosure Policy",
  content: <div className="space-y-4">
        <p>
          We do not share, sell, or lease client security data to any third parties. Disclosure only occurs under specific legal mandates or when required for federal law enforcement compliance.
        </p>
        <p>
          All sub-processors and infrastructure partners undergo rigorous security vetting and must adhere to our strict data handling and non-disclosure agreements.
        </p>
      </div>
}, {
  id: "changes",
  title: "6. Policy Retention & Updates",
  content: <div className="space-y-4">
        <p>
          This policy is reviewed quarterly to ensure continued compliance with evolving federal cybersecurity regulations and emerging threat landscapes.
        </p>
        <p>
          Clients will be notified 30 days in advance of any material changes to our data handling procedures via secure communication channels.
        </p>
      </div>
}];
export default function PrivacyPolicy() {
  return <section data-section-id="253558" id="privacy-policy" className="bg-background text-foreground py-16 md:py-24">
      <div className="container mx-auto px-4 md:px-6 max-w-4xl">
        
        {}
        <div className="mb-12 text-center md:text-left flex flex-col md:flex-row items-center md:items-start gap-4">
          <div className="bg-primary/10 p-4 rounded-lg shrink-0 shadow-md">
            <ShieldCheck className="size-10 text-primary" />
          </div>
          <div>
            <h1 className="text-3xl md:text-5xl font-bold font-default text-foreground mb-3 tracking-tight">
              Security & Privacy Protocol
            </h1>
            <p className="text-muted-foreground text-lg font-mono">
              Revision: 2024.04.12 | Federal Compliance Status: Active
            </p>
            <p className="text-muted-foreground mt-4 max-w-2xl leading-relaxed">
              This document outlines our rigorous standards for data encryption, federal security compliance, and the comprehensive procedures we employ to safeguard your organization's most sensitive information.
            </p>
          </div>
        </div>

        {}
        <div className="bg-card rounded-lg shadow-lg border border-border p-2 md:p-6">
          <Accordion type="multiple" defaultValue={["data-collection"]} className="w-full space-y-4">
            {policySections.map((section, index) => <AccordionItem key={section.id} value={section.id} data-index={index} className="border border-border rounded-md bg-background overflow-hidden border-l-4 border-l-primary data-[state=open]:border-l-accent transition-all duration-300 shadow-sm">
                <AccordionTrigger className="px-6 py-4 hover:bg-accent/5 hover:no-underline transition-colors">
                  <span className="text-lg font-semibold font-default text-foreground text-left">
                    {section.title}
                  </span>
                </AccordionTrigger>
                <AccordionContent className="px-6 pb-6 pt-2 text-muted-foreground leading-relaxed font-sans">
                  {section.content}
                </AccordionContent>
              </AccordionItem>)}
          </Accordion>
        </div>

        {}
        <div className="mt-12 p-8 bg-muted rounded-lg border border-border text-center shadow-md">
          <h3 className="text-xl font-bold font-default text-foreground mb-2">
            Compliance Inquiry?
          </h3>
          <p className="text-muted-foreground mb-6 max-w-md mx-auto">
            Our dedicated security audit team is available to discuss specific federal compliance requirements or data handling protocols.
          </p>
          <Link to="/ContactPage">
            <Button className="bg-primary text-primary-foreground hover:bg-primary/90 font-semibold px-8 py-6 rounded-md shadow-lg transition-all">
              Contact Security Team
            </Button>
          </Link>
        </div>

      </div>
    </section>;
}